import { useEffect, useState } from 'react'
import { Plus, Send, X } from 'lucide-react'
import type { AppSettings } from '@shared/types'
import { useStore } from '../../store'
import { api, integrations, type Skill, type McpServer } from '../../lib/api'
import { cn } from '../../lib/utils'
import { ViewHeader } from '../shell/ViewHeader'
import { BrokerSettings } from './BrokerSettings'
import { RobinhoodSettings } from './RobinhoodSettings'
import { Switch, TradingRiskSettings } from './TradingRiskSettings'
import { CommissionSettings } from './CommissionSettings'
import { InsuranceSettings } from './InsuranceSettings'
import { BandSettings } from './BandSettings'
import { XaiSubscriptionSettings } from './XaiSubscriptionSettings'
import { AutomationSettings } from './AutomationSettings'
import { ContextSettings } from './ContextSettings'
import { MarketDataUsage } from './MarketDataUsage'

const tgInput =
  'min-w-0 flex-1 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-3 py-2 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none'

function TelegramSettings() {
  const settings = useStore((s) => s.settings)
  const update = useStore((s) => s.updateSettings)
  const [token, setToken] = useState('')
  const [chatId, setChatId] = useState('')
  const [busy, setBusy] = useState<null | 'save' | 'detect' | 'test' | 'disconnect'>(null)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  const connected = settings?.telegramConfigured ?? false
  const isAdmin = useStore((s) => s.user?.role === 'admin') // trading is admin-only
  useEffect(() => setChatId(settings?.telegramChatId ?? ''), [settings?.telegramChatId])

  // Persist whatever is currently typed (token only if non-blank) before an action.
  const persist = async () => {
    const patch: Partial<AppSettings> = { telegramChatId: chatId.trim() }
    if (token.trim()) patch.telegramBotToken = token.trim()
    await update(patch)
    setToken('')
  }

  const run = (kind: NonNullable<typeof busy>, fn: () => Promise<string>) => async () => {
    setBusy(kind)
    setMsg(null)
    try {
      setMsg({ tone: 'ok', text: await fn() })
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Something went wrong' })
    } finally {
      setBusy(null)
    }
  }

  const onSave = run('save', async () => {
    await persist()
    return 'Saved.'
  })
  const onDetect = run('detect', async () => {
    await persist()
    const r = await api.telegramDetectChat()
    setChatId(r.chatId)
    await update({})
    return `Found chat: ${r.name} (${r.chatId})`
  })
  const onTest = run('test', async () => {
    await persist()
    await api.telegramTest()
    return 'Test message sent — check Telegram.'
  })
  const onDisconnect = run('disconnect', async () => {
    await api.telegramDisconnect()
    setChatId('')
    setToken('')
    await update({})
    return 'Disconnected.'
  })

  const btn = 'rounded-lg px-3 py-2 text-[12.5px] font-medium transition-colors disabled:opacity-40'

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-semibold text-fg">Telegram</h2>
        <span className={cn('rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide', connected ? 'bg-[var(--accent-wash)] text-fg' : 'text-fg-faint')}>
          {connected ? 'Connected' : 'Not connected'}
        </span>
      </div>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        Connect your own Telegram bot so the AI can message you — alerts, summaries, reminders. Create a bot with{' '}
        <b className="text-fg-muted">@BotFather</b>, paste its token below, then open your bot in Telegram and send it any
        message (e.g. <code className="rounded bg-[var(--color-inset)] px-1">/start</code>) and press <b className="text-fg-muted">Detect</b>.
      </p>

      <div className="mt-3 space-y-2">
        <input
          type="password"
          autoComplete="off"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder={connected ? 'Bot token stored — type to replace' : 'Bot token (e.g. 123456:ABC-DEF…)'}
          className={tgInput}
        />
        <div className="flex items-center gap-1.5">
          <input value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="Chat ID (or use Detect →)" className={cn(tgInput, 'num')} />
          <button onClick={onDetect} disabled={!!busy} className={cn(btn, 'shrink-0 border border-[var(--line-soft)] text-fg-muted hover:text-fg')}>
            {busy === 'detect' ? 'Detecting…' : 'Detect'}
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <button onClick={onSave} disabled={!!busy} className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onTest}
          disabled={!!busy || !chatId.trim() || (!connected && !token.trim())}
          className={cn(btn, 'inline-flex items-center gap-1.5 border border-[var(--line-soft)] text-fg-muted hover:text-fg')}
        >
          <Send size={13} /> {busy === 'test' ? 'Sending…' : 'Send test'}
        </button>
        {connected && (
          <button onClick={onDisconnect} disabled={!!busy} className={cn(btn, 'text-down hover:bg-[var(--color-surface-2)]')}>
            Disconnect
          </button>
        )}
      </div>

      {msg && <div className={cn('mt-2 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}

      {isAdmin && settings && (
        <div className={cn('mt-4 divide-y divide-[var(--separator)] rounded-lg border border-[var(--line-soft)]', !connected && 'opacity-60')}>
          <div className="flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]">
            <span className="min-w-0">
              <span className="text-fg-muted">Trade notifications</span>
              <span className="block text-[11px] text-fg-faint">
                When your workers trade: fills, orders placed at IBKR, a worker finishing (net of fees), and problems — in Turkish.
              </span>
            </span>
            <Switch on={settings.tradeNotify !== false} label="Trade notifications" onChange={(v) => void update({ tradeNotify: v })} />
          </div>
          <div className={cn('flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]', settings.tradeNotify === false && 'opacity-50')}>
            <span className="min-w-0">
              <span className="text-fg-muted">Include rule actions</span>
              <span className="block text-[11px] text-fg-faint">e.g. “TP1 filled → stops to 102.00”. Problems are always sent.</span>
            </span>
            <Switch on={settings.tradeNotifyRules !== false} label="Include rule actions" onChange={(v) => void update({ tradeNotifyRules: v })} />
          </div>
          {!connected && <div className="px-3 py-2 text-[11px] text-fg-faint">Connect a bot above to receive them.</div>}
        </div>
      )}
    </section>
  )
}

function BrowserDevicesSettings() {
  const [devices, setDevices] = useState<import('../../lib/api').BridgeDevice[]>([])
  const [label, setLabel] = useState('')
  const [newToken, setNewToken] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const serverUrl = window.location.origin

  const load = async () => {
    try {
      setDevices((await api.bridgeDevices()).devices)
    } catch {
      /* ignore */
    }
  }
  useEffect(() => {
    void load()
    const t = setInterval(load, 4000) // live connected status
    return () => clearInterval(t)
  }, [])

  const add = async () => {
    setBusy(true)
    try {
      const d = await api.bridgeAddDevice(label.trim() || 'My device')
      setNewToken(d.token)
      setLabel('')
      await load()
    } finally {
      setBusy(false)
    }
  }
  const revoke = async (id: string) => {
    await api.bridgeRevokeDevice(id)
    await load()
  }

  return (
    <section className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Browser fetch devices</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        Install the Liberabank Fetch Bridge extension on any browser and pair it here. Web fetches then run through
        <b className="text-fg-muted"> that browser&apos;s IP</b> (better for Cloudflare-protected sites), even when no
        Liberabank tab is open. Leave one running at home; requests from your other devices use it.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Device name (e.g. Home mini-PC)"
          className={tgInput}
        />
        <button
          onClick={add}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-2 text-[13px] font-medium text-[var(--color-base)] disabled:opacity-50"
        >
          <Plus size={14} /> Add device
        </button>
      </div>

      {newToken && (
        <div className="mt-3 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] p-3">
          <div className="text-[12px] text-fg-muted">
            Paste this into the extension (server URL <span className="num text-fg">{serverUrl}</span>). Shown once:
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <code className="num min-w-0 flex-1 truncate rounded bg-[var(--color-surface-2)] px-2 py-1.5 text-[12px] text-fg">{newToken}</code>
            <button
              onClick={() => void navigator.clipboard?.writeText(newToken)}
              className="rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:border-[var(--line)]"
            >
              Copy
            </button>
            <button onClick={() => setNewToken(null)} className="rounded-lg px-2 py-1.5 text-fg-faint hover:text-fg">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        {devices.length === 0 && <div className="text-[12.5px] text-fg-faint">No devices paired yet.</div>}
        {devices.map((d) => (
          <div key={d.id} className="flex items-center gap-2.5 rounded-lg border border-[var(--line-soft)] px-3 py-2">
            <span
              className={cn('h-2 w-2 shrink-0 rounded-full', d.connected ? 'bg-up' : 'bg-[var(--color-fg-faint)]')}
              title={d.connected ? 'Connected now' : 'Offline'}
            />
            <span className="min-w-0 flex-1 truncate text-[13px] text-fg-muted">{d.label}</span>
            <span className="shrink-0 text-[11px] text-fg-faint">{d.connected ? 'connected' : 'offline'}</span>
            <button onClick={() => void revoke(d.id)} className="shrink-0 rounded-lg px-2 py-1 text-[11px] text-fg-faint hover:text-down" title="Revoke">
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}

function SkillsSettings() {
  const [skills, setSkills] = useState<Skill[]>([])
  const [name, setName] = useState('')
  const load = () => integrations.skills().then(setSkills).catch(() => {})
  useEffect(() => { void load() }, [])
  const add = async () => {
    const n = name.trim()
    if (!n) return
    await integrations.skillCreate(n, '')
    setName('')
    await load()
  }
  const patch = (id: string, p: Partial<Skill>) => setSkills((xs) => xs.map((s) => (s.id === id ? { ...s, ...p } : s)))
  const save = (s: Skill) => integrations.skillUpdate(s.id, { name: s.name, instructions: s.instructions, enabled: s.enabled })
  const remove = async (id: string) => { await integrations.skillDelete(id); await load() }

  return (
    <section id="settings-skills" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Skills</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        Reusable instruction modules. When a skill is on, its instructions are added to the AI&apos;s system prompt for
        your chats — e.g. a house style for analysis, a checklist, or a workflow.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New skill name (e.g. Earnings deep-dive)" className={tgInput} onKeyDown={(e) => e.key === 'Enter' && void add()} />
        <button onClick={() => void add()} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-2 text-[13px] font-medium text-[var(--color-base)]">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="mt-3 space-y-2.5">
        {skills.length === 0 && <div className="text-[12.5px] text-fg-faint">No skills yet.</div>}
        {skills.map((s) => (
          <div key={s.id} className="rounded-lg border border-[var(--line-soft)] p-3">
            <div className="flex items-center gap-2">
              <input value={s.name} onChange={(e) => patch(s.id, { name: e.target.value })} className="min-w-0 flex-1 bg-transparent text-[13px] font-medium text-fg focus:outline-none" />
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[12px] text-fg-muted">
                <input type="checkbox" checked={s.enabled} onChange={(e) => { patch(s.id, { enabled: e.target.checked }); void integrations.skillUpdate(s.id, { enabled: e.target.checked }) }} className="accent-[var(--color-accent)]" />
                On
              </label>
              <button onClick={() => void remove(s.id)} className="shrink-0 rounded-lg px-1.5 py-1 text-fg-faint hover:text-down" title="Delete"><X size={14} /></button>
            </div>
            <textarea
              value={s.instructions}
              onChange={(e) => patch(s.id, { instructions: e.target.value })}
              onBlur={() => void save(s)}
              rows={8}
              placeholder="Instructions the AI should follow when this skill is on… (paste a full skill — up to ~50k chars)"
              className="mt-2 w-full resize-y rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-2 text-[12.5px] text-fg placeholder:text-fg-faint focus:outline-none"
            />
          </div>
        ))}
      </div>
    </section>
  )
}

function McpSettings() {
  const [servers, setServers] = useState<McpServer[]>([])
  const [label, setLabel] = useState('')
  const [url, setUrl] = useState('')
  const load = () => integrations.mcp().then(setServers).catch(() => {})
  useEffect(() => { void load() }, [])
  const add = async () => {
    if (!label.trim() || !/^https?:\/\//i.test(url.trim())) return
    await integrations.mcpCreate(label.trim(), url.trim(), '')
    setLabel(''); setUrl('')
    await load()
  }
  const remove = async (id: string) => { await integrations.mcpDelete(id); await load() }

  return (
    <section id="settings-mcp" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Connectors (MCP)</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        Add remote <b className="text-fg-muted">MCP</b> servers (Model Context Protocol). When enabled, the AI gets that
        server&apos;s tools natively. EODHD market data is already built in.
      </p>
      <div className="mt-3 space-y-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Name (e.g. My data server)" className={tgInput} />
        <div className="flex items-center gap-2">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://mcp.example.com/v1/mcp?apikey=…" className={tgInput} />
          <button onClick={() => void add()} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-2 text-[13px] font-medium text-[var(--color-base)]">
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {servers.length === 0 && <div className="text-[12.5px] text-fg-faint">No connectors added.</div>}
        {servers.map((m) => (
          <div key={m.id} className="flex items-center gap-2.5 rounded-lg border border-[var(--line-soft)] px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] text-fg-muted">{m.label}</div>
              <div className="num truncate text-[11px] text-fg-faint">{m.url}</div>
            </div>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[12px] text-fg-muted">
              <input type="checkbox" checked={m.enabled} onChange={(e) => { setServers((xs) => xs.map((x) => (x.id === m.id ? { ...x, enabled: e.target.checked } : x))); void integrations.mcpUpdate(m.id, { enabled: e.target.checked }) }} className="accent-[var(--color-accent)]" />
              On
            </label>
            <button onClick={() => void remove(m.id)} className="shrink-0 rounded-lg px-1.5 py-1 text-fg-faint hover:text-down" title="Delete"><X size={14} /></button>
          </div>
        ))}
      </div>
    </section>
  )
}

export function SettingsPage() {
  const settings = useStore((s) => s.settings)
  const settingsFocus = useStore((s) => s.settingsFocus)
  useEffect(() => {
    if (!settingsFocus) return
    const el = document.getElementById(`settings-${settingsFocus}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      el.classList.add('ring-1', 'ring-[var(--line-strong)]')
      const t = setTimeout(() => el.classList.remove('ring-1', 'ring-[var(--line-strong)]'), 1600)
      return () => clearTimeout(t)
    }
  }, [settingsFocus])
  const update = useStore((s) => s.updateSettings)
  const theme = useStore((s) => s.theme)
  const setTheme = useStore((s) => s.setTheme)
  const model = useStore((s) => s.health?.model)
  const isAdmin = useStore((s) => s.user?.role === 'admin')
  const [newSym, setNewSym] = useState('')

  if (!settings) return null
  const tickers = settings.tickerSymbols ?? []

  const addTicker = () => {
    const raw = newSym.trim().toUpperCase().replace(/\s+/g, '')
    if (!raw) return
    const sym = raw.includes('.') || raw.startsWith('^') ? raw : `${raw}.US`
    if (!tickers.includes(sym)) void update({ tickerSymbols: [...tickers, sym] })
    setNewSym('')
  }

  const input =
    'min-w-0 flex-1 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-3 py-2 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none'

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-[var(--color-base)]">
      <ViewHeader title="Settings" />
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto w-full max-w-2xl space-y-5 px-4 py-6 sm:px-5">

        <section className="card p-4">
          <h2 className="text-[14px] font-semibold text-fg">Ticker tape</h2>
          <p className="mt-0.5 text-[12.5px] text-fg-subtle">
            Which symbols show in the top bar. Use <code>.US</code> by default, or <code>^</code> for
            indices (e.g. <code>^VIX</code>).
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tickers.map((sym) => (
              <span key={sym} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] bg-[var(--color-surface-2)] px-2.5 py-1 text-[12px] text-fg-muted">
                {sym.replace(/\.US$/, '')}
                <button onClick={() => void update({ tickerSymbols: tickers.filter((t) => t !== sym) })} className="text-fg-subtle hover:text-down" title="Remove">
                  <X size={12} />
                </button>
              </span>
            ))}
            {tickers.length === 0 && <span className="text-[12px] text-fg-faint">No symbols.</span>}
          </div>
          <div className="mt-3 flex items-center gap-1.5">
            <input value={newSym} onChange={(e) => setNewSym(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTicker()} placeholder="Add symbol (e.g. AAPL, ^VIX)" className={input} />
            <button onClick={addTicker} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-accent)] text-[var(--color-base)]" title="Add">
              <Plus size={16} />
            </button>
          </div>
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13px] text-fg-muted">
            <input type="checkbox" checked={settings.showPortfolioInTicker} onChange={(e) => void update({ showPortfolioInTicker: e.target.checked })} className="accent-[var(--color-accent)]" />
            Show my total portfolio value in the ticker tape
          </label>
          <label className="mt-2 flex cursor-pointer items-center gap-2 text-[13px] text-fg-muted">
            <input type="checkbox" checked={settings.showHoldingsInTicker} onChange={(e) => void update({ showHoldingsInTicker: e.target.checked })} className="accent-[var(--color-accent)]" />
            Show each portfolio holding’s symbol in the ticker tape
          </label>
        </section>

        <section className="card p-4">
          <h2 className="text-[14px] font-semibold text-fg">Appearance</h2>
          <div className="mt-3 inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
            {(['dark', 'light'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={cn(
                  'rounded-md px-4 py-1.5 text-[12.5px] font-medium capitalize transition-colors',
                  theme === t ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg',
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </section>

        <section className="card p-4">
          <h2 className="text-[14px] font-semibold text-fg">AI</h2>
          <div className="mt-2 flex items-center justify-between text-[13px]">
            <span className="text-fg-muted">Model</span>
            <span className="num text-fg-subtle">{model ?? '—'}</span>
          </div>
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13px] text-fg-muted">
            <input type="checkbox" checked={settings.liveSearch} onChange={(e) => void update({ liveSearch: e.target.checked })} className="accent-[var(--color-accent)]" />
            Use live web + X research by default
          </label>
        </section>

        {isAdmin && <XaiSubscriptionSettings />}
        {isAdmin && <MarketDataUsage />}

        {isAdmin && <BrokerSettings />}
        {isAdmin && <RobinhoodSettings />}
        {isAdmin && <CommissionSettings />}
        {isAdmin && <InsuranceSettings />}
        {isAdmin && <BandSettings />}
        {isAdmin && <TradingRiskSettings />}
        <ContextSettings />
        <AutomationSettings />
        <SkillsSettings />
        <McpSettings />
        <TelegramSettings />
        <BrowserDevicesSettings />
        </div>
      </div>
    </div>
  )
}
